import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CsInputMeasureComponent } from '../components/cs-input-measure/cs-input-measure';
import { KeyboardOptions } from './virtual-keyboard.service';

interface PairInfo {
  primary: CsInputMeasureComponent;
  secondary: CsInputMeasureComponent;
  pairId: string;
}

// How a dual-roller value lands on the paired inputs. Preview runs on every
// wheel tick and only updates the inputs' visible text; Commit additionally
// emits editDone, which triggers the expensive page-level work (e.g. the
// plate-loading solver) and therefore only runs once the wheel settles (#4042)
enum RollerApplyMode {
  Preview = 'preview',
  Commit = 'commit',
}

@Injectable({ providedIn: 'root' })
export class InputPairingService implements OnDestroy {
  private pairs = new Map<CsInputMeasureComponent, PairInfo>();
  private destroyed$ = new Subject<void>();
  private nextPairId = 0;

  registerPair(primary: CsInputMeasureComponent, secondary: CsInputMeasureComponent): void {
    // Check if already registered
    if (this.pairs.has(primary) || this.pairs.has(secondary)) {
      console.warn('Components already registered in a pair');
      return;
    }

    const pairId = `pair-${this.nextPairId++}`;
    const pairInfo: PairInfo = { primary, secondary, pairId };

    // Register both components
    this.pairs.set(primary, pairInfo);
    this.pairs.set(secondary, pairInfo);

    // Set up cleanup when components are destroyed
    if (primary.destroyed$) {
      primary.destroyed$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.unregisterPair(primary);
      });
    }
    if (secondary.destroyed$) {
      secondary.destroyed$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.unregisterPair(secondary);
      });
    }
  }

  private unregisterPair(component: CsInputMeasureComponent): void {
    const pairInfo = this.pairs.get(component);
    if (pairInfo) {
      this.pairs.delete(pairInfo.primary);
      this.pairs.delete(pairInfo.secondary);
    }
  }

  getPairedComponent(component: CsInputMeasureComponent): CsInputMeasureComponent | null {
    const pairInfo = this.pairs.get(component);
    if (!pairInfo) return null;

    return component === pairInfo.primary ? pairInfo.secondary : pairInfo.primary;
  }

  getRole(component: CsInputMeasureComponent): 'primary' | 'secondary' | null {
    const pairInfo = this.pairs.get(component);
    if (!pairInfo) return null;

    return component === pairInfo.primary ? 'primary' : 'secondary';
  }

  isPaired(component: CsInputMeasureComponent): boolean {
    return this.pairs.has(component);
  }

  // Full commit of a dual-roller selection: updates both inputs' text AND
  // emits editDone on both (which triggers the expensive page-level work).
  // Called once the wheel settles, and on the direct commit paths.
  handleDualRollerSelection(
    component: CsInputMeasureComponent,
    value1: number,
    value2: number
  ): void {
    this.applyDualRollerSelection(component, value1, value2, RollerApplyMode.Commit);
  }

  // Lightweight per-tick preview of a dual-roller selection: updates both
  // inputs' visible text only, no editDone. Runs on every wheel tick, so it
  // must stay cheap (#4042).
  previewDualRollerSelection(
    component: CsInputMeasureComponent,
    value1: number,
    value2: number
  ): void {
    this.applyDualRollerSelection(component, value1, value2, RollerApplyMode.Preview);
  }

  private applyDualRollerSelection(
    component: CsInputMeasureComponent,
    value1: number,
    value2: number,
    mode: RollerApplyMode
  ): void {
    const pairInfo = this.pairs.get(component);
    if (!pairInfo) return;

    const paired = this.getPairedComponent(component);
    if (!paired) return;

    // Determine which component has units (and thus goes first in the
    // roller). This is the same condition generateRollerOptionsForPairing
    // uses to include unitConfigs - checked directly here because
    // regenerating both components' full roller value lists on every commit
    // just to read this one flag was a per-tick hotspot in #4042
    const thisHasUnits = (component.measurementUnitConfigs?.length || 0) > 0;
    const pairedHasUnits = (paired.measurementUnitConfigs?.length || 0) > 0;
    const isThisFirst = thisHasUnits || (!thisHasUnits && !pairedHasUnits && component === pairInfo.primary);

    // Assign values based on the roller order
    let thisValue: number;
    let pairedValue: number;

    if (isThisFirst) {
      // This component is first in roller (value1)
      thisValue = value1;
      pairedValue = value2;
    } else {
      // Paired component is first in roller (value1)
      thisValue = value2;
      pairedValue = value1;
    }

    this.applyValueToComponent(component, thisValue, mode);
    this.applyValueToComponent(paired, pairedValue, mode);
  }

  // Write one roller value onto one input component: convert from display to
  // canonical units, set the visible text, and (in Commit mode only) emit
  // editDone so the page reacts to the final value
  private applyValueToComponent(
    component: CsInputMeasureComponent,
    displayValue: number,
    mode: RollerApplyMode
  ): void {
    const nominalUnit = component.measurementType?.nominalUnit || 1;
    const unitValue = component.measurementUnitConfig?.value1 || 1;
    const canonicalValue = displayValue * (unitValue / nominalUnit);

    const texts = component.canonValueToTexts_internal(canonicalValue);
    component.text1 = texts[0];
    component.text2 = texts[1] || '';
    component.onTextsChange();
    if (mode === RollerApplyMode.Commit) {
      component.editDone.emit(component.canonValue);
    }
    component.cdr.markForCheck();
  }

  getKeyboardOptionsForPair(component: CsInputMeasureComponent): Partial<KeyboardOptions> | null {
    const pairInfo = this.pairs.get(component);
    if (!pairInfo) return null;

    const paired = this.getPairedComponent(component);
    if (!paired) return null;

    // Both components must support roller mode
    if (!component.useInputScroller() || !paired.useInputScroller() ||
        component.forceKeypadInput || paired.forceKeypadInput ||
        !component.preselectValues || !paired.preselectValues) {
      return null;
    }

    const isThisPrimary = component === pairInfo.primary;
    let primary = isThisPrimary ? component : paired;
    let secondary = isThisPrimary ? paired : component;

    // Get options from both components
    const primaryOptions = primary.generateRollerOptionsForPairing();
    const secondaryOptions = secondary.generateRollerOptionsForPairing();

    // Determine which component is primary (has units) and which is secondary
    const primaryHasUnits = (primaryOptions.unitConfigs?.length || 0) > 0;
    const secondaryHasUnits = (secondaryOptions.unitConfigs?.length || 0) > 0;

    // Build dual roller configuration
    const options: Partial<KeyboardOptions> = {
      isDualRoller: true,
      startInRollerMode: true,
    };

    // primary component goes first
    options.preselectValuesNumeric1 = primaryOptions.preselectValuesNumeric1 || [];
    options.preselectValuesNumeric2 = secondaryOptions.preselectValuesNumeric1 || [];
    // Don't default to 0 - keep undefined if no current value so suggested value is used
    options.currentValueNumeric1 = primaryOptions.currentValueNumeric1;
    options.currentValueNumeric2 = secondaryOptions.currentValueNumeric1;
    options.suggestedValueNumeric1 = primaryOptions.suggestedValueNumeric1 || 0;
    options.suggestedValueNumeric2 = secondaryOptions.suggestedValueNumeric1 || 0;
    options.rollerLabel1 = primary.label?.toUpperCase() ||
      (primary as any).getRollerLabel?.() ||
      primary.measurementUnitConfig?.unit1?.toUpperCase() ||
      '';
    options.rollerLabel2 = secondary.label?.toUpperCase() ||
      (secondary as any).getRollerLabel?.() ||
      secondary.measurementUnitConfig?.unit1?.toUpperCase() ||
      '';

    // Use primary component's unit configuration if it has units
    options.unitConfigs = primaryOptions.unitConfigs || [];
    options.currentUnitId = primaryOptions.currentUnitId || '';

    // Set up unit change handler - updates whichever component has units
    const unitComponent = primaryHasUnits ? primary : (secondaryHasUnits ? secondary : null);
    options.onUnitChange = async (unitId: string) => {
      if (unitComponent) {
        unitComponent.userUnit = unitId;
        const config = unitComponent.measurementUnitConfigs?.find(c => c.id === unitId);
        if (config) {
          unitComponent.measurementUnitConfig = config;
        }
        unitComponent.cdr.markForCheck();
        unitComponent.userUnitChange.emit(unitId);

        // Wait for Angular to process the change
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    };

    // Set up value selection handler
    options.onValueSelectedNumeric = (values: { value1: number; value2?: number }) => {
      this.handleDualRollerSelection(primary, values.value1, values.value2 || 0);
    };

    // Per-tick preview: text only, no editDone (#4042)
    options.onValuePreviewNumeric = (values: { value1: number; value2?: number }) => {
      this.previewDualRollerSelection(primary, values.value1, values.value2 || 0);
    };

    return options;
  }


  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.pairs.clear();
  }
}